import { useCallback, useRef, useState } from 'react';
import {
  deploySurveyInCloud,
  undeployInCloud,
  updateDeploymentSlugInCloud,
  shareSurveyInCloud,
  deleteSurveyInCloud,
  copySurveyInCloud,
  listDeploymentUsersFromCloud,
  getSurveySummaryFromCloud,
} from '../utils/cloudApi';
import { linkUserToDeployment } from '../utils/consoleApi';
import { getOrganization } from '../utils/accountApi';

/**
 * Survey lifecycle actions (refactor Step 5b): deploy, undeploy, re-deploy,
 * re-send, share, copy, delete, custom slug. All handler logic moved verbatim
 * from App.js; the hook owns the WP3 undeploy-confirmation machinery, the
 * re-deploy modal state, and the bulk-delete progress counter.
 *
 * Params (App-owned context and UI bridges):
 *  - user: from useAuth() (org-profile gate on deploy).
 *  - surveys: for display names in bulk-delete errors.
 *  - staging: the useStagingArea hook (re-send stages the fresh deployment).
 *  - refresh, rememberDeploymentCounts: from useSurveyData.
 *  - selectedSurveyId, onSelectSurvey, onSelectDeployment: selection bridge.
 *  - setLoading, onError, setConfirmModal: App-global UI.
 *  - refreshDeploymentUsers: () => Promise — UsersPanel reload bridge.
 *
 * Returns the handlers plus modal state for App to render:
 *  - undeployPrompt + resolveUndeploy(ok): the completion-checked undeploy
 *    ConfirmModal (server-decided; styled modal, never a native dialog).
 *  - redeployModal, setRedeployModal: the BUG-106 re-deploy confirmation.
 *  - deleteProgress: { current, total } during bulk survey delete.
 */
export default function useSurveyLifecycle({
  user,
  surveys,
  staging,
  refresh,
  rememberDeploymentCounts,
  selectedSurveyId,
  onSelectSurvey,
  onSelectDeployment,
  setLoading,
  onError,
  setConfirmModal,
  refreshDeploymentUsers,
}) {
  // Undeploy (WP3 §3): always succeeds, silent or confirmed per the server's
  // completion check. We call /undeploy {confirmed:false}; if the server says
  // confirmation is required (x≠y, or an anonymous link), we surface a styled
  // ConfirmModal — never a native dialog — and, on confirm, repeat the call
  // with {confirmed:true}. The Promise-based modal lets a bulk loop await each
  // decision sequentially.
  const undeployConfirmResolverRef = useRef(null);
  const [undeployPrompt, setUndeployPrompt] = useState(null); // { deploymentId, counts }
  // Re-deploy modal state (BUG-106)
  const [redeployModal, setRedeployModal] = useState(null);
  const [deleteProgress, setDeleteProgress] = useState(null); // { current, total } during bulk survey delete

  const resolveUndeploy = useCallback((confirmed) => {
    undeployConfirmResolverRef.current?.(confirmed);
  }, []);

  const reportDeploymentFailure = (message) => {
    const text = String(message || 'The survey could not be deployed.');
    if (!setConfirmModal) {
      onError(text);
      return;
    }
    // Deployment errors must be impossible to mistake for an ignored drop.
    // Use the app modal rather than the small global notification banner.
    setConfirmModal({
      title: 'Survey could not be deployed',
      message: text,
      confirmLabel: 'Done',
      cancelLabel: null,
      onConfirm: () => setConfirmModal(null),
      onCancel: () => setConfirmModal(null),
    });
  };

  const onDeploy = async (surveyId) => {
    // A drop is an explicit deployment request. Start visible feedback before
    // either preflight request so validation never looks like a failed drag.
    setLoading(true);
    onError('');

    // Org profile completeness gate: deployment requires a complete
    // Organizational Profile (formal name, logo, contact URL) so recipients
    // can identify who is running the survey. This hook sits outside
    // OrgProvider, so fetch the org record directly rather than via useOrg().
    try {
      const org = await getOrganization(user?.org_id);
      const missingFields = [];
      if (!org?.formal_name?.trim()) missingFields.push('Legal / Formal Name');
      if (!org?.logo_url) missingFields.push('Organization Logo');
      const contactUrl = (org?.contact_url || '').trim();
      if (!contactUrl) {
        missingFields.push('Organization Contact URL');
      } else if (!contactUrl.startsWith('https://')) {
        missingFields.push('Organization Contact URL (must start with https://)');
      }
      if (missingFields.length > 0) {
        reportDeploymentFailure(`Your Organization Profile is incomplete. Please set: ${missingFields.join(', ')}. Go to Account → Organization Profile.`);
        setLoading(false);
        return;
      }
    } catch (e) {
      reportDeploymentFailure(`Unable to verify your Organization Profile (${e.message}).`);
      setLoading(false);
      return;
    }

    // Survey header integrity check: required metadata fields. The Survey
    // Designer saves these inside descriptor.survey_descriptor, not as
    // top-level survey columns. Read the same fields, with the same
    // fallbacks, as the server-side check in CreateDeployment so the two
    // checks always agree.
    try {
      const summary = await getSurveySummaryFromCloud(surveyId);
      const headers = summary?.descriptor?.survey_descriptor || {};
      const title = String(headers.survey_name || summary?.title || summary?.name || '').trim();
      const description = String(headers.survey_purpose || summary?.description || '').trim();
      const author = String(headers.author || '').trim();
      const duration = String(headers.estimated_time || '').trim();

      const missing = [];
      if (!title) missing.push('Survey Title');
      if (!description) missing.push('Survey Description / Purpose');
      if (!author) missing.push('Author');
      if (!duration) missing.push('Estimated Duration');

      if (missing.length > 0) {
        reportDeploymentFailure(`Survey headers must include ${missing.join(', ')}. Use the Survey Designer to add these elements.`);
        setLoading(false);
        return;
      }
    } catch (e) {
      reportDeploymentFailure(`Unable to verify survey headers (${e.message}).`);
      setLoading(false);
      return;
    }

    // Slug is always auto-generated at deploy time. A custom slug can be
    // assigned afterwards via the "Custom" button in the Deployed panel.
    try {
      const info = await deploySurveyInCloud({ surveyId });
      const depId = info?.deployment_id || info?.id;
      await refresh();
      if (depId) {
        onSelectSurvey(surveyId);
        onSelectDeployment(depId);
        // UsersPanel will auto-refresh via useEffect on selectedDeploymentId change;
        // also trigger explicitly to ensure immediate refresh after deploy
        await refreshDeploymentUsers();
      }
    } catch (e) {
      reportDeploymentFailure(e.message);
    } finally { setLoading(false); }
  };

  // Share a survey with another account (Ready-to-deploy "Share" button).
  // The server snapshots the survey and holds it pending until the
  // recipient accepts; tier rules are enforced server-side.
  const onShareSurvey = async (surveyId, toEmail, message) => {
    setLoading(true); onError('');
    try {
      await shareSurveyInCloud({ surveyId, toEmail, message });
    } catch (e) {
      onError(e.message);
    } finally { setLoading(false); }
  };

  // Assign a custom URL slug to a deployed survey (Deployed panel "Custom" button).
  const onSetCustomSlug = async (deploymentId, slug) => {
    setLoading(true); onError('');
    try {
      await updateDeploymentSlugInCloud({ deploymentId, slug });
      await refresh();
    } catch (e) {
      onError(e.message);
    } finally { setLoading(false); }
  };

  const onUndeploy = async (deploymentId) => {
    if (!deploymentId) return;
    setLoading(true); onError('');
    try {
      let res = await undeployInCloud({ deploymentId, confirmed: false });
      if (res?.counts) rememberDeploymentCounts(deploymentId, res.counts);

      if (res && res.requires_confirmation && !res.closed) {
        const confirmed = await new Promise((resolve) => {
          undeployConfirmResolverRef.current = resolve;
          setUndeployPrompt({ deploymentId, counts: res.counts || {} });
        });
        setUndeployPrompt(null);
        undeployConfirmResolverRef.current = null;
        if (!confirmed) return; // author cancelled — survey stays live
        res = await undeployInCloud({ deploymentId, confirmed: true });
        if (res?.counts) rememberDeploymentCounts(deploymentId, res.counts);
      }
      await refresh();
    } catch (e) {
      onError(e.message);
    } finally { setLoading(false); }
  };

  // Re-send an already-SENT survey dropped into the Staging Area. The server
  // lifecycle (ready → deployed → staged → sent) forbids staging a 'sent'
  // survey directly, so to make it sendable again we run the full round-trip:
  //   undeploy (closes the current wave, completion-checked) → deploy (fresh
  //   deployment) → stage (deployed → staged). The staged recipient selection
  //   (staging.stagedIds) is client-side and left untouched, so the next Send
  //   dispatches to the same people against the NEW deployment. Reuses the same
  //   undeployPrompt/ConfirmModal machinery as onUndeploy for the confirm step.
  const reSendSentSurvey = useCallback(async (survey) => {
    const currentDeploymentId = survey?.deploymentId;
    const surveyId = survey?.surveyId;
    if (!currentDeploymentId || !surveyId) {
      onError('Cannot re-send: the dropped survey is missing its survey or deployment id.');
      return;
    }
    setLoading(true); onError('');
    try {
      // (a) Undeploy the current wave. Completion-checked: if the server says
      // confirmation is required (recipients may still respond), raise the SAME
      // styled ConfirmModal as onUndeploy. Cancel => abort, leave the survey live.
      let res = await undeployInCloud({ deploymentId: currentDeploymentId, confirmed: false });
      if (res?.counts) rememberDeploymentCounts(currentDeploymentId, res.counts);
      if (res && res.requires_confirmation && !res.closed) {
        const confirmed = await new Promise((resolve) => {
          undeployConfirmResolverRef.current = resolve;
          setUndeployPrompt({ deploymentId: currentDeploymentId, counts: res.counts || {} });
        });
        setUndeployPrompt(null);
        undeployConfirmResolverRef.current = null;
        if (!confirmed) return; // author cancelled — do NOT stage; survey stays live
        res = await undeployInCloud({ deploymentId: currentDeploymentId, confirmed: true });
        if (res?.counts) rememberDeploymentCounts(currentDeploymentId, res.counts);
      }
      // (b) Deploy a fresh wave. The survey is now 'ready' with no open
      // deployment, so deploy succeeds and returns the new deployment id.
      const info = await deploySurveyInCloud({ surveyId });
      const newDepId = info?.deployment_id || info?.id;
      if (!newDepId) throw new Error('Re-deploy succeeded but no deployment id was returned; cannot stage for re-send.');
      // (c) Stage the new deployment and record it in the Staging Area pointing
      // at newDepId. The new deployment is 'deployed', so linkSurvey's normal
      // stage path succeeds; the staged recipient selection is unchanged, so the
      // subsequent Send targets newDepId.
      await staging.linkSurvey({ ...survey, deploymentId: newDepId });
      // (d) Refresh so panels reflect the new live deployment.
      await refresh();
    } catch (e) {
      onError(`Could not re-deploy “${survey?.surveyName || 'survey'}” for send: ${e.message}`);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staging, rememberDeploymentCounts, refresh]);

  // Re-deploy a dispatched survey: fetch the original deployment's linked
  // users, then open the standard deployment-confirmation modal so the user
  // can review the recipient list and confirm. On confirm
  // (onConfirmRedeploy), a new deployment is created and each original
  // recipient is re-linked to it. Sending invitations is intentionally NOT
  // triggered automatically — the user can run the standard Send flow from
  // the Staging Area on the new deployment if they want to dispatch again.
  // (BUG-106)
  const onRedeploy = async (surveyId, originalDeploymentId, surveyName) => {
    if (!surveyId || !originalDeploymentId) return;
    setLoading(true); onError('');
    try {
      const result = await listDeploymentUsersFromCloud(originalDeploymentId);
      const items = result.items || result || [];
      const recipients = items.map(u => ({
        id: u.user_id || u.recipient_id,
        displayName: u.name || u.email || u.user_id || u.recipient_id,
        channel: u.channel || u.target_channel || '',
        email: u.email || '',
      })).filter(r => r.id);
      setRedeployModal({ surveyId, originalDeploymentId, surveyName, recipients });
    } catch (e) {
      onError(`Failed to load recipients for re-deploy: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const onConfirmRedeploy = async () => {
    if (!redeployModal) return;
    const { surveyId, recipients } = redeployModal;
    setRedeployModal(null);
    setLoading(true); onError('');
    try {
      const info = await deploySurveyInCloud({ surveyId });
      const newDepId = info?.deployment_id || info?.id;
      if (!newDepId) throw new Error('New deployment was created but the deployment_id was not returned');
      let linkFailures = 0;
      for (const r of recipients) {
        try {
          await linkUserToDeployment(newDepId, r.id, r.email, r.displayName);
        } catch (linkErr) {
          linkFailures += 1;
          console.warn(`[Re-deploy] Failed to link ${r.id}:`, linkErr.message);
        }
      }
      await refresh();
      onSelectSurvey(surveyId);
      onSelectDeployment(newDepId);
      if (linkFailures > 0) {
        onError(`Re-deployed, but ${linkFailures} of ${recipients.length} recipient link(s) failed. Check the console for details.`);
      }
    } catch (e) {
      onError(`Re-deploy failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const onDeleteSurvey = (surveyId) => {
    // Open ConfirmModal instead of window.confirm (BUG-113).
    setConfirmModal({
      title: 'Delete survey?',
      message: 'Permanently delete this survey? This is only allowed if it has no open deployments.',
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        setConfirmModal(null);
        setLoading(true); onError('');
        try {
          await deleteSurveyInCloud({ surveyId });
          if (selectedSurveyId === surveyId) onSelectSurvey(null);
          await refresh();
        } catch (e) { onError(e.message); }
        finally { setLoading(false); }
      },
    });
  };

  // Bulk delete: sequential, single loading/refresh cycle, error aggregation.
  // Mirrors onBulkArchiveSurveys. The panel already shows its own confirmation
  // dialog, so this performs the deletes directly without re-confirming each
  // one. The server refuses to delete a survey that has open deployments, so
  // those come back as per-survey errors rather than silently succeeding.
  const onBulkDeleteSurveys = async (surveyIds) => {
    if (!surveyIds || surveyIds.length === 0) return;

    setLoading(true);
    onError('');

    const total = surveyIds.length;
    setDeleteProgress({ current: 0, total });

    const failures = []; // { surveyName, message }
    let deleted = 0;
    let alreadyGone = 0;

    for (let i = 0; i < surveyIds.length; i++) {
      const surveyId = surveyIds[i];
      const survey = (surveys || []).find(s => s.survey_id === surveyId);
      const surveyName = survey?.name || survey?.title || surveyId;
      setDeleteProgress({ current: i, total });
      try {
        const res = await deleteSurveyInCloud({ surveyId });
        deleted++;
        if (res?.alreadyGone) alreadyGone++;
      } catch (e) {
        failures.push({ surveyName, message: e.message });
      }
    }

    setDeleteProgress({ current: total, total });
    onSelectSurvey(null);
    await refresh();

    if (failures.length > 0) {
      const lines = failures.map(f => `• ${f.surveyName}: ${f.message}`);
      onError(`Deleted ${deleted}/${total}. ${failures.length} failed:\n${lines.join('\n')}`);
    }

    setDeleteProgress(null);
    setLoading(false);
  };

  const onCopy = async (surveyId) => {
    setLoading(true); onError('');
    try {
      await copySurveyInCloud({ surveyId });
      await refresh();
    } catch (e) { onError(e.message); }
    finally { setLoading(false); }
  };

  return {
    // modal/progress state for App to render
    undeployPrompt,
    resolveUndeploy,
    redeployModal, setRedeployModal,
    deleteProgress,
    // actions
    onDeploy,
    onShareSurvey,
    onSetCustomSlug,
    onUndeploy,
    reSendSentSurvey,
    onRedeploy,
    onConfirmRedeploy,
    onDeleteSurvey,
    onBulkDeleteSurveys,
    onCopy,
  };
}
